<script setup lang="ts">
import type { DesignModel, DistributionGroup } from '@forgd/contract/types'
import DtDistributionPreviewChart from '#design-tokenomics/components/DtDistributionPreviewChart.vue'
import DtDistributionSummary from '#design-tokenomics/components/DtDistributionSummary.vue'
import DtDistributionTable from '#design-tokenomics/components/DtDistributionTable.vue'
import DtViewReferences from '#design-tokenomics/components/DtViewReferences.vue'
import { useDesignDocument } from '#design-tokenomics/composables/useDesignDocument'
import { useWorkingGroups } from '#design-tokenomics/composables/useWorkingGroups'
import { formatValue } from '@forgd/formatter'
import { round } from '@forgd/shared/utils/numbers'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, PlusIcon } from '@heroicons/vue/24/outline'

const model = defineModel<DesignModel>({ required: true })

const { ticker } = useDesignDocument()
const { groups, addGroup, deleteGroup, replaceGroups } = useWorkingGroups(model)

const MAX_GROUPS = 20

const totalAllocation = computed(() =>
  round(groups.value.reduce((sum, group) => sum + (group.allocationPercentage || 0), 0), 4),
)

const pendingPercent = computed(() => round(100 - totalAllocation.value, 4))

const state = computed(() => {
  if (totalAllocation.value < 100) {
    return 'allocation-pending'
  }

  return totalAllocation.value > 100 ? 'allocation-exceeded' : 'allocation-complete'
})

const canAddGroup = computed(() => groups.value.length < MAX_GROUPS)

function onAddGroup() {
  addGroup()
}

function setGroups(next: DistributionGroup[]) {
  replaceGroups(next)
}
</script>

<template>
  <div class="space-y-5">
    <div class="flex flex-col gap-5 xl:flex-row">
      <Card class="grow">
        <CardHeader>
          <CardTitle class="text-sm">
            {{ ticker ? `${ticker} Groups` : 'Groups' }}
          </CardTitle>
        </CardHeader>

        <CardContent class="p-0">
          <div class="space-y-5 text-sm">
            <DtDistributionTable v-model="groups" @delete-group="deleteGroup" />
          </div>
        </CardContent>

        <CardFooter>
          <div class="flex w-full justify-end">
            <Button
              size="sm"
              variant="outline"
              class="w-50 justify-center font-display"
              :disabled="!canAddGroup"
              data-testid="dt-add-group"
              @click="onAddGroup"
            >
              Add New Group
              <PlusIcon aria-hidden="true" />
            </Button>
          </div>
        </CardFooter>
      </Card>

      <div class="w-full space-y-5 xl:w-114">
        <DtDistributionSummary :groups="groups" />
        <DtDistributionPreviewChart :groups="groups" :total-allocation="totalAllocation">
          <template #header-action>
            <DtViewReferences @select="setGroups" />
          </template>
        </DtDistributionPreviewChart>
      </div>
    </div>

    <Alert v-if="state === 'allocation-complete'" variant="success">
      <CheckCircleIcon />
      <AlertTitle>Great job. 100% of the total token supply has been allocated.</AlertTitle>
      <AlertDescription>
        <p>If you'd like to add additional groups, please ensure that the total percentage allocated to your groups does not exceed 100%.</p>
      </AlertDescription>
    </Alert>

    <Alert v-else-if="state === 'allocation-exceeded'" variant="warning">
      <ExclamationTriangleIcon />
      <AlertTitle>The total quantity of tokens allocated exceeds 100% of the indicated total token supply.</AlertTitle>
      <AlertDescription>
        <p>Please review the values before continuing to the next step.</p>
      </AlertDescription>
    </Alert>

    <Alert v-else variant="info">
      <InformationCircleIcon />
      <AlertTitle>{{ formatValue(pendingPercent, 'percent') }} of the total token supply is still pending allocation.</AlertTitle>
      <AlertDescription>
        <p>Please complete 100% of the allocation to mark the task as complete.</p>
      </AlertDescription>
    </Alert>
  </div>
</template>
